import { memo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Form, Input, Popconfirm, Typography, message } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { numToKorean } from 'num-to-korean';

import OutbidByPrice from './OutbidByPrice';
import OutbidByTime from './OutbidByTime';

import { createBid } from 'apis/bid';
import { Auction } from 'models/auction/Auction';

import { formatCurrency } from 'utils';

import styles from './CreateBidForm.module.css';
import { ApiError } from 'apis/ApiError';
import { openBidsListParam } from '../BidsList/constants';

const { Title } = Typography;

interface CreateBidFormProps {
  auction: Auction;
  setAuction: (auction: Auction) => void;
  closeCreateBidForm: () => void;
}

function CreateBidForm({ auction, setAuction, closeCreateBidForm }: CreateBidFormProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const showBidsList = () => {
    searchParams.set(openBidsListParam.key, openBidsListParam.value);
    setSearchParams(searchParams);
  };

  const [form] = Form.useForm();
  const [invitationCode, setInvitationCode] = useState<string>('');
  const [invitationCodeErrMsg, setInvitationCodeErrMsg] = useState<string | null>(null);
  const [price, setPrice] = useState<number | undefined>(undefined);
  const [priceErrMsg, setPriceErrMsg] = useState<string | null>(null);

  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const [isBiddingLoading, setIsBiddingLoading] = useState(false);
  const [openBidConfirm, setOpenBidConfirm] = useState(false);
  const [outbidMessage, setOutbidMessage] = useState<React.ReactNode>(null);

  const getNaturalPrice = () => {
    if (price === undefined) return '';

    if (isNaN(price) || price === 0) return '0 원';

    return `${numToKorean(price)} 원`;
  };

  const handleInvitationCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInvitationCode(e.target.value);
    setInvitationCodeErrMsg(null);
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrice(+e.target.value);
    setPriceErrMsg(null);
  };

  const handleCreateBid = () => {
    if (isBiddingLoading) return;

    setIsBiddingLoading(true);

    createBid(auction.id, price, invitationCode)
      .then(({ bid, bid_result }) => {
        setAuction(bid.auction);
        setPrice(undefined);
        form.setFieldValue('price', undefined);
        if (bid_result === 'confirmed') {
          message.success('입찰되었습니다.');
          closeCreateBidForm();
          showBidsList();
        } else if (bid_result === 'outbid_by_price') {
          setOutbidMessage(<OutbidByPrice disclosedPrice={bid.disclosed_price} />);
          message.warning('더 높은 입찰 금액의 참여자가 있습니다!');
        } else if (bid_result === 'outbid_by_time') {
          setOutbidMessage(<OutbidByTime />);
          message.warning('더 먼저 입찰한 참여자가 있습니다!');
        }
      })
      .catch((errMsg) => {
        if (errMsg) setPriceErrMsg(errMsg);
      })
      .finally(() => {
        setIsConfirmLoading(false);
        setIsBiddingLoading(false);
        setOpenBidConfirm(false);
      });
  };

  const handleSubmit = async () => {
    if (isConfirmLoading) return;

    if (!price || (auction.invitation_code_required && !invitationCode)) {
      if (!price) setPriceErrMsg('입찰 금액을 입력해주세요');
      if (!invitationCode) setInvitationCodeErrMsg('초대 코드를 입력해주세요');
      return;
    }

    setIsConfirmLoading(true);

    createBid(auction.id, price, invitationCode, true)
      .then(() => setOpenBidConfirm(true))
      .catch((err?: ApiError) => {
        const priceErrMsg = err?.errors?.price?.[0] ?? null;
        const invitationCodeErrMsg = err?.errors?.invitation_code?.[0] ?? null;
        const generalErrMsg = err?.message;

        setPriceErrMsg(priceErrMsg);
        setInvitationCodeErrMsg(invitationCodeErrMsg);

        if (!priceErrMsg && !invitationCodeErrMsg && generalErrMsg) {
          setPriceErrMsg(generalErrMsg);
        }

        setIsConfirmLoading(false);
      });
  };

  const handleCancelConfirm = () => {
    setOpenBidConfirm(false);
    setIsConfirmLoading(false);
  };

  return (
    <>
      <Title level={3}>입찰 금액을 입력해주세요</Title>
      {outbidMessage}
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {auction.invitation_code_required && (
          <Form.Item
            label="초대 코드"
            name="invitationCode"
            required
            help={invitationCodeErrMsg}
            validateStatus={invitationCodeErrMsg ? 'error' : undefined}
          >
            <Input
              size="large"
              placeholder="초대 코드를 입력해주세요"
              value={invitationCode}
              onChange={handleInvitationCodeChange}
            />
          </Form.Item>
        )}
        <Form.Item
          label="입찰 금액"
          name="price"
          required
          help={priceErrMsg}
          validateStatus={priceErrMsg ? 'error' : undefined}
        >
          <Input
            type="number"
            size="large"
            addonBefore="₩"
            placeholder="입찰할 금액을 입력해주세요"
            value={price}
            onChange={handlePriceChange}
          />
        </Form.Item>
        <Form.Item className="no-margin">
          <Popconfirm
            open={openBidConfirm}
            overlayClassName={styles.confirm}
            title="입찰은 취소할 수 없습니다!"
            description={
              <>
                <div>
                  <b>{`입찰 금액 : ${formatCurrency(price as number)} (${getNaturalPrice()})`}</b>
                </div>
                <div>
                  낙찰 후 연락이 되지 않거나 물품 대금이 지급되지 않을 경우 향후 경매 참여가 제한될 수 있습니다. 정말
                  입찰하시겠습니까?
                </div>
              </>
            }
            okText="입찰 확인"
            okButtonProps={{ loading: isBiddingLoading, disabled: isBiddingLoading }}
            cancelText="취소"
            onConfirm={handleCreateBid}
            onCancel={handleCancelConfirm}
          >
            <Button
              block
              type="primary"
              htmlType="submit"
              icon={<DollarOutlined />}
              loading={isConfirmLoading}
              disabled={isConfirmLoading}
            >
              {`${getNaturalPrice()} 입찰`}
            </Button>
          </Popconfirm>
        </Form.Item>
      </Form>
    </>
  );
}

export default memo(CreateBidForm);
